import { Divider, MenuItem, Stack, ThemeProvider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { moreMenuTheme } from '@/modules/common/theme';
import {
  PencilIcon,
  ReloadIcon,
  VisualizationIcon,
  DeleteIcon,
  ForkliftIcon,
  DownloadIcon,
} from '@/assets/icons';
import { Menu } from './Menu';
import { DashboardMenuItem } from './DashboardMenuItem';

type Props = {
  status: string;
  simulationRunId: string;
  dashboardUrl?: string;
  dashboardUrlExpirationDate?: Date | null;
  onViewClick: () => void;
  onEditClick?: () => void;
  onRerunClick?: () => void;
  onDeleteClick?: () => void;
  onDownloadSimulationFilesClick?: () => void;
};

/**
 * Top buttons for single simulations type
 */
export const TopButtons = ({
  status,
  simulationRunId,
  dashboardUrl,
  dashboardUrlExpirationDate,
  onEditClick,
  onRerunClick,
  onViewClick,
  onDeleteClick,
  onDownloadSimulationFilesClick,
}: Props) => {
  const { t } = useTranslation('interface');
  const showViewBtn = status === 'COMPLETED' || (status === 'FAILED' && simulationRunId);

  return (
    <Stack
      sx={{
        alignItems: 'center',
        flexDirection: 'row',
        height: '100%',
        justifyContent: 'flex-end',
        gap: 1,
      }}
    >
      <ThemeProvider theme={moreMenuTheme}>
        <Menu>
          {showViewBtn && (
            <div>
              <MenuItem disabled>
                <ForkliftIcon width={24} />
                {t('simulation.run_card.commissioning.label')}
              </MenuItem>
              <MenuItem onClick={onViewClick}>
                <VisualizationIcon />
                {`${t('simulation.run_card.view_button.label')}`}
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
            </div>
          )}
          <DashboardMenuItem url={dashboardUrl} expirationDate={dashboardUrlExpirationDate} />
          {(onRerunClick || onEditClick) && <Divider sx={{ my: 0.5 }} />}

          {onEditClick && (
            <MenuItem onClick={onEditClick}>
              <PencilIcon />
              {t('simulation.run_card.edit_button.label')}
            </MenuItem>
          )}
          {onRerunClick && (
            <MenuItem
              onClick={onRerunClick}
              disabled={status !== 'COMPLETED' && status !== 'PARTIALLY_COMPLETED'}
            >
              <ReloadIcon />
              {t('simulation.run_card.rerun_button.label')}
            </MenuItem>
          )}
          {onDeleteClick && (
            <MenuItem onClick={onDeleteClick}>
              <DeleteIcon width={24} />
              {t('simulation.run_card.delete_button.tooltip')}
            </MenuItem>
          )}
          {onDownloadSimulationFilesClick && (
            <MenuItem onClick={onDownloadSimulationFilesClick}>
              <DownloadIcon width={24} />
              {t('simulation.run_card.download_simulation_files.label')}
            </MenuItem>
          )}
        </Menu>
      </ThemeProvider>
    </Stack>
  );
};
